import React, { useState, useEffect, useMemo } from 'react';
import { MDBCol, MDBRow } from 'mdbreact';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { actionSvgs } from 'assets/icons';
import If from 'components/If';
import ContentLoader from 'components/Informations/Loaders/ContentLoader';
import PageStructure from 'components/PageStructure';

import FilterField from 'components/FormGroup/FilterField';
import SelectField from 'components/FormGroup/SelectField';
import InformationCard from 'components/Cards/InformationCard';

import { totallyHours } from 'utils/moment';

import history from 'utils/history';
import endpoints from 'services/endpoints';
import useFetch from 'hooks/useFetch';

import * as S from './styles';

const { getAppointmentPendencies } = endpoints;

export default function EmployeePendenciesPage({ user }) {
  const { id_funcionario: userID } = user;

  const [employees, setEmployees] = useState([]);
  const [searchedEmployees, setSearchedEmployees] = useState([]);
  const [selectSearchion, setSelectSearchion] = useState('');
  const [inputSearchion, setInputSearchion] = useState('');

  const [allPendencies, allPendenciesLoading] = useFetch(
    `${getAppointmentPendencies}/${userID}`,
  );

  const functionOptions = useMemo(() => {
    const allFunctions = _.uniqBy(
      employees?.map(({ nome_funcao }) => ({
        value: nome_funcao,
        label: nome_funcao,
      })),
      'value',
    );

    const orderedFunctions = allFunctions?.sort((a, b) =>
      a.value > b.value ? 1 : -1,
    );

    return selectSearchion
      ? [{ value: 0, label: 'Limpar filtro' }, ...orderedFunctions]
      : [...orderedFunctions];
  }, [employees, selectSearchion]);

  const totalWorkedHours = useMemo(() => {
    const workedHours = employees?.map(({ horas_trabalhadas }) =>
      Number(horas_trabalhadas),
    );
    return totallyHours(_.sum(workedHours)) || '00:00';
  }, [employees]);

  const totalNotPointedHours = useMemo(() => {
    const notPointedHours = employees?.map(({ horas_nao_apontadas }) =>
      Number(horas_nao_apontadas),
    );
    return totallyHours(_.sum(notPointedHours)) || '00:00';
  }, [employees]);

  const memoizedFunctionsSearch = useMemo(
    () =>
      employees?.filter(({ nome_funcao }) => nome_funcao === selectSearchion),
    [selectSearchion, employees],
  );

  useEffect(() => {
    if (!allPendenciesLoading) {
      const sortedEmployees = JSON.parse(allPendencies)?.sort((a, b) =>
        a.funcionario > b.funcionario ? 1 : -1,
      );

      setEmployees(sortedEmployees);
    }
  }, [allPendencies, allPendenciesLoading]);

  const handleInputSearch = () => {
    if (inputSearchion === '') return [];

    const filtered = employees?.filter(
      employee =>
        employee?.funcionario
          ?.toLowerCase()
          .indexOf(inputSearchion.toLowerCase()) >= 0,
    );

    return setSearchedEmployees(filtered);
  };

  const renderEmployeesTable = data => (
    <S.Table>
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Profissional</th>
              <th scope="col">Horas trabalhadas</th>
              <th scope="col">Horas não apontadas</th>
              <th scope="col">Ações</th>
            </tr>
          </thead>

          <tbody>
            {data.map(employee => (
              <tr key={employee.id_funcionario}>
                <td>{employee.funcionario}</td>
                <td>{totallyHours(employee.horas_trabalhadas)}</td>
                <td>{totallyHours(employee.horas_nao_apontadas)}</td>
                <td>
                  <S.ViewAppointments
                    to={{
                      pathname: '/auth/appointments/admin',
                      state: employee,
                    }}
                  >
                    {actionSvgs('view')}
                  </S.ViewAppointments>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </S.Table>
  );

  return (
    <PageStructure
      title="Funcionários com pendências de apontamento"
      subtitle="Verifique abaixo os funcionários com horas trabalhadas que não estão apontadas em projetos"
      previousPage="/auth/approve-appointments"
      pageAction={() => history.goBack()}
    >
      <MDBRow>
        <MDBCol lg="4" md="12">
          <InformationCard
            counter={totalWorkedHours}
            description="Horas trabalhadas"
          />
        </MDBCol>
        <MDBCol lg="4" md="12">
          <InformationCard
            counter={totalNotPointedHours}
            description="Horas não apontadas"
          />
        </MDBCol>
      </MDBRow>

      <MDBRow>
        <MDBCol lg="3" md="12">
          <S.FilterWrapper>
            <FilterField
              labelText="Busque um funcionário"
              placeholderText="Busque um funcionário"
              name="employees-filter"
              handleChange={({ target }) => {
                setInputSearchion(target.value);
                handleInputSearch(target.value);
              }}
              value={inputSearchion}
              isDisabled={memoizedFunctionsSearch.length > 0}
            />
          </S.FilterWrapper>
        </MDBCol>

        <MDBCol lg="3" md="12">
          <S.FilterWrapper>
            <SelectField
              hasSecondaryStyle
              name="functions-filter"
              value={functionOptions.find(
                ({ value }) => value === selectSearchion,
              )}
              options={functionOptions}
              handleChange={({ value }) => setSelectSearchion(value)}
              labelText="Pesquise por função"
              placeholderText="Selecione uma função..."
              isDisabled={!!inputSearchion}
            />
          </S.FilterWrapper>
        </MDBCol>
      </MDBRow>

      <MDBRow>
        <MDBCol lg="12">
          <If
            condition={
              searchedEmployees.length > 0 &&
              memoizedFunctionsSearch.length <= 0
            }
          >
            {renderEmployeesTable(searchedEmployees)}
          </If>

          <If
            condition={
              searchedEmployees.length <= 0 &&
              memoizedFunctionsSearch.length <= 0
            }
          >
            {renderEmployeesTable(employees)}
          </If>

          <If condition={memoizedFunctionsSearch.length > 0}>
            {renderEmployeesTable(memoizedFunctionsSearch)}
          </If>
        </MDBCol>
      </MDBRow>

      <If condition={allPendenciesLoading}>
        <ContentLoader isFull />
      </If>
    </PageStructure>
  );
}

EmployeePendenciesPage.defaultProps = {
  user: {},
};

EmployeePendenciesPage.propTypes = {
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
